import { IonRouterLink } from "@ionic/react";
import { type FormikHelpers } from "formik";
import { useState } from "react";
import { type SocietyAuthBodyInput } from "shared/api/types/society/[societyId]/auth";
import PillarForm from "shared/components/pillar-form/PillarForm";
import PillarModalBody from "shared/components/pillar-modal/NewPillarModalBody";
import { type PillarModalControlAsync } from "shared/components/pillar-modal/usePillarModalControl";
import { sanitizeHtml } from "shared/components/template/sanitize-html";
import { useCustomRouter } from "shared/context/CustomRouterContext";
import { useCustomSession } from "shared/context/CustomSessionContext";
import { findRoute } from "shared/helpers/routes/findRoute";
import * as Yup from "yup";
import ContentZone from "hub/src/components/common/ContentZone";
import { SocietyLogo } from "hub/src/components/common/SocietyLogo";
import { useAuthentification } from "hub/src/hooks/useAuthentification";
import useDeviceInfo from "hub/src/hooks/useDeviceInfo";
import { type LoginPageRouteParams } from "hub/src/pages/LoginPage";
import { type HubRouteConfig } from "hub/src/routes/RouteConfigWrapper";

type LoginModalProps = {
  control: PillarModalControlAsync<null>;
};

export const LoginModal = ({ control }: LoginModalProps) => {
  const device = useDeviceInfo();
  const router = useCustomRouter();
  const session = useCustomSession();

  const [formSubmitted, setFormSubmitted] = useState(false);

  const routeConfig = findRoute(session?.society?.routes ?? [], {
    url: "/login",
  }) as unknown as HubRouteConfig<LoginPageRouteParams>;

  const { waitForAuthentification } = useAuthentification();

  const handleSubmit = async (
    values: Required<SocietyAuthBodyInput>,
    formikHelpers?: FormikHelpers<Required<SocietyAuthBodyInput>> | undefined,
  ) => {
    setFormSubmitted(true);
    try {
      await session.login!(values);
      await session.refreshSession(router.pathname);
      await waitForAuthentification();
      control.resolve(null);
    } catch (error: unknown) {
      setFormSubmitted(false);
      formikHelpers?.setStatus({
        error: "Incorrect email or password. Please try again.",
      });
    }
  };

  return (
    <PillarModalBody data-testid="login-modal" control={control}>
      <div
        className="h-full w-full max-w-1/4 min-w-6xl m-auto
          flex flex-col justify-center items-center
          phone:min-w-full phone:max-w-none"
      >
        {!!routeConfig?.contentPage?.length &&
          !!routeConfig?.id &&
          routeConfig?.contentPage?.length > 0 && (
            <ContentZone
              zone="top"
              contentPages={routeConfig.contentPage}
              appRouteConfigId={routeConfig.id}
            />
          )}

        {session.society?.societySettingsPublic?.societyLogoLoginUrl ? (
          <SocietyLogo className="w-[100px] h-[100px] mb-2 !border-society-400" />
        ) : (
          <div className="w-12.5 h-12.5 mb-7 rounded-full bg-primary flex justify-center items-center">
            <span className="text-5xl text-extrabold text-neutral-light">
              {session.society?.abbreviation}
            </span>
          </div>
        )}

        <h2 className="mb-4.5">
          {routeConfig?.routeParams?.headingText ?? "Log In"}
        </h2>

        {!!routeConfig?.contentPage?.length &&
          routeConfig?.contentPage?.length > 0 &&
          !!routeConfig?.id && (
            <ContentZone
              zone="middle"
              contentPages={routeConfig.contentPage}
              appRouteConfigId={routeConfig.id}
            />
          )}

        <div className="py-4">
          <PillarForm<Required<SocietyAuthBodyInput>>
            id="login-form"
            handleSubmit={handleSubmit}
            className="flex flex-col w-full"
            yupValidation={Yup.object().shape({
              email: Yup.string().email().required().label("Email"),
              password: Yup.string().required(),
              returnUrl: Yup.string().url(),
            })}
          >
            <PillarForm.EmailInput
              name="email"
              autoComplete="email"
              label={routeConfig?.routeParams?.emailLabel ?? "Email address"}
              required
              data-testid={"login-form-email"}
            />
            <PillarForm.PasswordInput
              name="password"
              autoComplete="password"
              label={routeConfig?.routeParams?.passwordLabel ?? "Password"}
              required
              data-testid={"login-form-password"}
            />
            <PillarForm.LoadingSubmitButton
              className="button-regular-general-filled w-full mt-4"
              testid="login-form-submit"
            >
              {routeConfig?.routeParams?.buttonText ?? "Log In"}
            </PillarForm.LoadingSubmitButton>

            {device.isWeb && (
              <PillarForm.Button className="button-regular-general-unfilled mt-4 group">
                <IonRouterLink
                  routerLink={
                    routeConfig?.routeParams?.signUpPath ?? "/account/new"
                  }
                >
                  <div
                    data-testid="signup-link"
                    dangerouslySetInnerHTML={{
                      __html: sanitizeHtml(
                        routeConfig?.routeParams?.signUpHtml ??
                          "Don't have an account? <span class='font-bold'>Sign Up!</span>",
                      ),
                    }}
                  />
                </IonRouterLink>
              </PillarForm.Button>
            )}

            <PillarForm.Button className="button-regular-general-unfilled mt-4 group">
              <IonRouterLink
                routerLink={
                  routeConfig?.routeParams?.recoverPath ?? "/account/recover"
                }
              >
                <div
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHtml(
                      routeConfig?.routeParams?.recoverHtml ??
                        "Forgot Login? <span class='font-bold'>Click to recover!</span>",
                    ),
                  }}
                />
              </IonRouterLink>
            </PillarForm.Button>
          </PillarForm>
        </div>

        {!!routeConfig?.contentPage?.length &&
          routeConfig?.contentPage?.length > 0 &&
          !!routeConfig?.id && (
            <ContentZone
              zone="bottom"
              contentPages={routeConfig.contentPage}
              appRouteConfigId={routeConfig.id}
            />
          )}
      </div>
    </PillarModalBody>
  );
};
